'use client';

import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';

function ProjectFiles({ organizationId, projectId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFiles = async () => {
    if (!organizationId || !projectId) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch all files for the organization and project
      const { data, error } = await supabase.storage
        .from('pdf-reports')
        .list(`reports/${organizationId}/${projectId}`, {
          limit: 100, // Adjust this limit as needed
          offset: 0,
        });

      if (error) throw error;

      // Map the file paths to include download URLs and metadata
      const fileDetails = await Promise.all(
        data.map(async (file) => {
          const { data: url, error: urlError } = await supabase.storage
            .from('pdf-reports')
            .getPublicUrl(`reports/${organizationId}/${projectId}/${file.name}`);

          if (urlError) throw urlError;

          return {
            name: file.name,
            date: new Date(file.created_at).toLocaleDateString(), // Adjust date format if needed
            url: url.publicUrl,
          };
        })
      );

      setFiles(fileDetails);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Failed to load files.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [organizationId, projectId]);

  if (loading) return <div>Loading files...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="p-4">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Project Files</h2>
      {files.length === 0 ? (
        <p className="text-sm text-gray-500">No files available for this project.</p>
      ) : (
        <div className="overflow-hidden rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-300 bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Date</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {files.map((file) => (
                <tr key={file.name}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{file.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{file.date}</td>
                  <td className="px-6 py-4 text-right">
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 text-sm font-medium hover:underline"
                    >
                      Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ProjectFiles;
